import React, { useState, useEffect } from 'react';
import { Download, Edit, Trash, Check, X, Image as ImageIcon, Package } from 'lucide-react';
import { ProcessedImage, SelectedSize } from '../types';

export interface ImagePreviewProps {
  image: ProcessedImage;
  selectedSizes: SelectedSize[];
  onDownload: (size: SelectedSize) => void;
  onBulkDownload?: () => void;
  onRemove?: (id: string) => void;
  onNameChange?: (id: string, newName: string) => void;
  isProcessing: boolean;
}

export function ImagePreview({ 
  image, 
  selectedSizes,
  onDownload,
  onBulkDownload,
  onRemove,
  onNameChange,
  isProcessing
}: ImagePreviewProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(image.name);
  const [isCached, setIsCached] = useState(false);
  
  // Görüntünün önbellekte olup olmadığını kontrol et
  useEffect(() => {
    const checkCache = async () => {
      if ('caches' in window) {
        try {
          const cache = await caches.open('dpi-converter-images');
          
          // Seçili boyutlar için önbellek anahtarları
          const activeSizes = selectedSizes.filter(s => s.isSelected);
          
          // Her bir boyut için önbellekte olup olmadığını kontrol et
          for (const size of activeSizes) {
            // Önbellek anahtarı oluştur (processClientSide fonksiyonundaki aynı formatta)
            const cacheKey = `image-${image.id}-${size.width}x${size.height}-jpeg-300dpi`;
            const cacheMatch = await cache.match(cacheKey);
            
            if (cacheMatch) {
              setIsCached(true);
              return;
            }
          }
          
          setIsCached(false);
        } catch (err) {
          console.error('Cache check error:', err);
          setIsCached(false);
        }
      }
    };
    
    checkCache();
  }, [image.id, selectedSizes]);
  
  const activeSizes = selectedSizes.filter(s => s.isSelected);
  
  const handleEditSubmit = () => {
    if (onNameChange) {
      onNameChange(image.id, editValue);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleEditSubmit();
    } else if (e.key === 'Escape') {
      setEditValue(image.name);
      setIsEditing(false);
    }
  };

  // Dosya boyutunu formatla
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // "Remove" butonunu işleme için güvenli handleRemove fonksiyonu
  const handleRemove = () => {
    if (onRemove) {
      onRemove(image.id);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="flex flex-col sm:flex-row">
        {/* Responsive görüntü önizleme */}
        <div className="w-full sm:w-1/3 lg:w-1/4 bg-gray-50 p-2 flex items-center justify-center relative">
          <img 
            src={image.preview} 
            alt={image.name}
            className="max-h-32 max-w-full object-contain"
          />
          
          {/* Önbellek işareti */}
          {isCached && (
            <div className="absolute top-1 right-1 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
              Cached
            </div>
          )}
        </div>
        
        {/* Duyarlı metin bilgileri */}
        <div className="p-3 flex-1 flex flex-col">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-2">
            {isEditing ? (
              <form onSubmit={handleEditSubmit} className="flex-1">
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="p-1 border border-gray-300 rounded w-full"
                  autoFocus
                />
              </form>
            ) : (
              <div 
                className="font-medium text-gray-900 cursor-pointer hover:text-blue-600"
                onClick={() => setIsEditing(true)}
              >
                {image.name}
              </div>
            )}
            
            {/* Mobilde düğme yerleşimi */}
            <div className="flex mt-2 sm:mt-0 w-full sm:w-auto">
              {/* onRemove tanımlı ise butonu göster */}
              {onRemove && (
                <button 
                  onClick={handleRemove}
                  className="text-sm text-gray-500 hover:text-red-500 touch-target flex items-center px-2"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
          
          <div className="text-xs text-gray-500 mb-2">
            {image.dimensions.width && image.dimensions.height ? (
              <span>{image.dimensions.width} × {image.dimensions.height}px</span>
            ) : (
              <span>Dimensions unknown</span>
            )}
            <span className="mx-2">•</span>
            <span>{formatFileSize(image.size)}</span>
            {image.dpi && (
              <>
                <span className="mx-2">•</span>
                <span>{image.dpi} DPI</span>
              </>
            )}
          </div>
          
          {/* Mobil uyumlu boyut listesi */}
          {activeSizes.length > 0 && (
            <div className="mt-2">
              <div className="text-xs font-medium text-gray-700">Selected sizes:</div>
              <div className="flex flex-wrap gap-1 mt-1">
                {activeSizes.map(size => {
                  const sizeKey = `${size.name}_${image.fileType}`;
                  const estimatedSize = image.estimatedSizes && image.estimatedSizes[sizeKey];
                  
                  return (
                    <div 
                      key={size.name}
                      className="text-xs bg-gray-100 rounded px-2 py-1 flex items-center"
                    >
                      <span>
                        {size.width}×{size.height}"
                        {estimatedSize && <span className="ml-1 text-gray-500">
                          (~{formatFileSize(estimatedSize)})
                        </span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}